import { motion } from 'framer-motion';
import { ShoppingBagIcon, HeartIcon, CheckIcon, XIcon } from './icons';
import { CartIcon } from './CartIcon';
import { WishlistIcon } from './WishlistIcon';
import type { Product } from '../types/api';
import type { CartItem } from '../types/app';

interface ProductsPanelProps {
  products: Product[];
  cartItems: CartItem[];
  onReserve: (product: Product) => void;
  onRemoveFromCart: (productId: number) => void;
  isLoading?: boolean;
  onClose?: () => void;
  cartCount: number;
  onCartClick: () => void;
  // Wishlist props (SPEC-033 / SPEC-064). Card-level "is this product favorited"
  // check — ProductsPanel deliberately uses product-level semantics (a card
  // represents "the product", and is favorited if ANY variant is wishlisted).
  // Per-variant discrimination happens inside the PDP (SPEC-064 R5).
  isWishlisted: (productId: number) => boolean;
  onToggleFavorite: (product: Product) => void;
  wishlistCount: number;
  onWishlistClick: () => void;
  onProductClick: (product: Product) => void;
  // SPEC-053: per-card loading state for Reserve button
  reservingProductId?: number | null;
}

export function ProductsPanel({
  products,
  cartItems,
  onReserve,
  onRemoveFromCart,
  isLoading = false,
  onClose,
  cartCount,
  onCartClick,
  isWishlisted,
  onToggleFavorite,
  wishlistCount,
  onWishlistClick,
  onProductClick,
  reservingProductId = null,
}: ProductsPanelProps) {
  const isInCart = (productId: number) =>
    cartItems.some((item) => item.product.id === productId);

  const handleReserve = (product: Product) => {
    if (isInCart(product.id)) {
      onRemoveFromCart(product.id);
    } else {
      onReserve(product);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col h-full">
        <div className="p-8 border-b border-brand-gold/20">
          <h2 className="text-2xl font-serif font-light text-brand-navy tracking-wide">
            Featured Products
          </h2>
        </div>
        <div className="flex-1 overflow-y-auto p-8 space-y-8">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse">
              <div className="w-full aspect-[4/5] bg-gray-200 rounded-sm mb-4" />
              <div className="h-3 bg-gray-200 rounded w-1/3 mb-2" />
              <div className="h-5 bg-gray-200 rounded w-2/3 mb-2" />
              <div className="h-4 bg-gray-200 rounded w-1/4" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Panel Header */}
      <div className="p-8 border-b border-brand-gold/20">
        <div className="flex items-center justify-between gap-4">
          <div className="flex-1">
            <h2 className="text-2xl font-serif font-light text-brand-navy tracking-wide mb-1">
              Featured Products
            </h2>
            <p className="text-xs text-brand-navy/60 tracking-wide font-sans">
              Selected for you
            </p>
          </div>
          <div className="flex items-center gap-3">
            <WishlistIcon count={wishlistCount} onClick={onWishlistClick} />
            <CartIcon count={cartCount} onClick={onCartClick} />
            {onClose && (
              <button
                onClick={onClose}
                className="lg:hidden p-2 hover:bg-brand-gold/10 rounded-full transition-colors"
              >
                <XIcon size={20} className="text-brand-navy/60" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Products List */}
      <div className="flex-1 overflow-y-auto p-8 space-y-8">
        {products.map((product, index) => {
          const inCart = isInCart(product.id);
          const isFavorite = isWishlisted(product.id);

          return (
            <motion.div
              key={product.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 + index * 0.15, duration: 0.6 }}
              className="group"
            >
              {/* Product Image */}
              <div
                className="relative aspect-[4/5] bg-white rounded-sm overflow-hidden mb-4 shadow-md cursor-pointer"
                onClick={() => onProductClick(product)}
              >
                <img
                  src={product.image_url}
                  alt={product.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                {/* Hover gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                {/* In Cart Badge */}
                {inCart && (
                  <motion.div
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="absolute top-4 right-4 bg-brand-gold rounded-full p-2 shadow-lg"
                  >
                    <CheckIcon size={16} className="text-white" strokeWidth={2.5} />
                  </motion.div>
                )}
              </div>

              {/* Product Info */}
              <div className="space-y-3">
                <div>
                  {product.collection && (
                    <p className="text-xs uppercase tracking-[0.15em] text-brand-gold font-sans mb-1">
                      {product.collection}
                    </p>
                  )}
                  <h3
                    className="text-lg text-brand-navy font-serif cursor-pointer hover:text-brand-gold transition-colors"
                    onClick={() => onProductClick(product)}
                  >
                    {product.title}
                  </h3>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-base text-brand-navy/80 font-sans">
                    {product.price}
                  </span>
                  {product.compare_at_price && (
                    <span className="text-sm text-gray-400 line-through font-sans">
                      {product.compare_at_price}
                    </span>
                  )}
                </div>
                {/* Action Buttons */}
                <div className="flex gap-2 pt-2">
                  {(() => {
                    const isReserving = reservingProductId === product.id;
                    return (
                      <button
                        onClick={() => handleReserve(product)}
                        disabled={isReserving}
                        aria-busy={isReserving}
                        className={`flex-1 px-4 py-3 text-xs tracking-[0.15em] uppercase transition-all duration-500 font-sans ${
                          isReserving
                            ? 'bg-brand-navy/50 text-white cursor-not-allowed'
                            : inCart
                            ? 'bg-brand-gold text-white hover:bg-brand-gold-dark'
                            : 'bg-brand-navy text-white hover:bg-brand-gold'
                        }`}
                      >
                        {isReserving ? (
                          <>
                            <svg
                              className="inline animate-spin mr-2"
                              width={14}
                              height={14}
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth={2}
                              aria-hidden="true"
                            >
                              <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                              <path d="M12 2a10 10 0 0 1 10 10" />
                            </svg>
                            Reserving…
                          </>
                        ) : inCart ? (
                          <>
                            <CheckIcon size={14} className="inline mr-2" strokeWidth={2} />
                            Reserved
                          </>
                        ) : (
                          <>
                            <ShoppingBagIcon size={14} className="inline mr-2" strokeWidth={1.5} />
                            Reserve
                          </>
                        )}
                      </button>
                    );
                  })()}
                  <button
                    onClick={() => onToggleFavorite(product)}
                    className={`px-4 py-3 border text-xs transition-all duration-500 ${
                      isFavorite
                        ? 'border-brand-gold bg-brand-gold/10 text-brand-gold'
                        : 'border-brand-navy/20 text-brand-navy hover:bg-brand-gold/10 hover:border-brand-gold'
                    }`}
                  >
                    <HeartIcon
                      size={14}
                      strokeWidth={1.5}
                      fill={isFavorite ? 'currentColor' : 'none'}
                    />
                  </button>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
